import React, { useEffect, useRef } from "react";
import {
  Autosuggest,
  Box,
  Button,
  DatePicker,
  DatePickerProps,
  FormField,
  Input,
  Multiselect,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import { OmsReport } from "../../lusca-search-api/generated-src";
import {
  OmsFilterCondition,
  OmsFilterCriteriaState,
} from "../../hooks/useOmsFilterCriteria";

const calendarStrings = {
  todayAriaLabel: "Today",
  nextMonthAriaLabel: "Next month",
  previousMonthAriaLabel: "Previous month",
  nextYearAriaLabel: "Next year",
  previousYearAriaLabel: "Previous year",
  currentMonthAriaLabel: "Current month",
  openCalendarAriaLabel: (date: string | null) =>
    date ? `Choose date, selected ${date}` : "Choose date",
};

function OMSDatePicker({
  focusRef,
  ...props
}: DatePickerProps & {
  focusRef?: (control: { focus: () => void } | null) => void;
}) {
  const container = useRef<HTMLDivElement>(null);
  const { ariaLabel = "Choose date" } = props;
  useEffect(() => {
    const root = container.current;
    if (!root) return undefined;
    // This component version names the calendar grid, but omits its popup dialog name.
    const namePopup = () =>
      root
        .querySelector('[role="dialog"]')
        ?.setAttribute("aria-label", `${ariaLabel} calendar`);
    const observer = new MutationObserver(namePopup);
    observer.observe(root, { childList: true, subtree: true });
    namePopup();
    return () => observer.disconnect();
  }, [ariaLabel]);
  return (
    <div ref={container}>
      <DatePicker {...props} ref={focusRef} />
    </div>
  );
}

export default function OMSFilterValue({
  report,
  criterion,
  index,
  update,
  registerFocus,
}: {
  report: OmsReport;
  criterion: OmsFilterCondition;
  index: number;
  update: OmsFilterCriteriaState["update"];
  registerFocus?: (control: { focus: () => void } | null) => void;
}) {
  const label = `Value ${index + 1}`;
  const type = report.columnTypes?.[criterion.column] || "string";
  const values = report.columnValues?.[criterion.column]?.values || [];
  const options = values.map((value) => ({
    value,
    label: value === "" ? "(Blank)" : value,
  }));
  const change = (value: string) => update(criterion.id, { value });
  function scalarControl(control: React.ReactElement) {
    return criterion.valueNotice &&
      type === "string" &&
      ["eq", "ne"].includes(criterion.operator) ? (
      <SpaceBetween size="xs">
        {control}
        <Button onClick={() => change("")}>Use blank value</Button>
      </SpaceBetween>
    ) : (
      control
    );
  }
  function composeDateTime(date: string, time: string, zone: string) {
    const timezone = !zone || zone.toUpperCase() === "UTC" ? "Z" : zone;
    change(`${date}T${time}${timezone}`);
  }
  if (["isNull", "isNotNull"].includes(criterion.operator))
    return <Box color="text-body-secondary">No value required.</Box>;
  if (["in", "notIn"].includes(criterion.operator)) {
    const selected = criterion.values || [];
    return (
      <Multiselect
        ref={registerFocus}
        expandToViewport
        ariaLabel={label}
        ariaLabelledby=""
        placeholder="Choose values"
        filteringType="auto"
        filteringPlaceholder="Search values"
        filteringAriaLabel={`Search values ${index + 1}`}
        filteringClearAriaLabel="Clear value search"
        selectedAriaLabel="Selected"
        noMatch="No matching values."
        empty="No suggested values."
        selectedOptions={selected.map((value) => ({
          value,
          label: value === "" ? "(Blank)" : value,
        }))}
        options={[
          ...options,
          ...selected
            .filter((value) => !values.includes(value))
            .map((value) => ({
              value,
              label: value === "" ? "(Blank)" : value,
            })),
        ]}
        keepOpen
        tokenLimit={3}
        deselectAriaLabel={(option) =>
          `Remove ${option.label || option.value || "value"}`
        }
        i18nStrings={{
          tokenLimitShowMore: "Show more",
          tokenLimitShowFewer: "Show fewer",
        }}
        onChange={({ detail }) =>
          update(criterion.id, {
            values: detail.selectedOptions.map((option) => option.value || ""),
          })
        }
      />
    );
  }
  if (type === "date")
    return (
      <OMSDatePicker
        focusRef={registerFocus}
        ariaLabel={label}
        ariaLabelledby=""
        value={criterion.value || ""}
        format="iso"
        placeholder="YYYY-MM-DD"
        i18nStrings={calendarStrings}
        onChange={({ detail }) => change(detail.value)}
      />
    );
  if (type === "datetime") {
    const raw = criterion.value || "";
    const [date, rest = ""] = raw.split("T");
    const storedZone = rest.match(/(?:[A-Za-z]+|[+-].*)$/)?.[0] || "";
    const zone = storedZone || (rest ? "Z" : "");
    const time = storedZone ? rest.slice(0, -storedZone.length) : rest;
    return (
      <SpaceBetween size="xs">
        <OMSDatePicker
          focusRef={registerFocus}
          ariaLabel={`Date ${index + 1}`}
          ariaLabelledby=""
          value={date}
          format="iso"
          placeholder="YYYY-MM-DD"
          i18nStrings={calendarStrings}
          onChange={({ detail }) =>
            composeDateTime(detail.value, time || "00:00:00", zone || "Z")
          }
        />
        <div className="oms-report-download__time">
          <FormField label="Time">
            <Input
              ariaLabel={`Time ${index + 1}`}
              value={time}
              placeholder="HH:mm:ss"
              onChange={({ detail }) =>
                composeDateTime(date, detail.value, zone || "Z")
              }
            />
          </FormField>
          <FormField label="Timezone">
            <Input
              ariaLabel={`Timezone ${index + 1}`}
              value={zone === "Z" ? "UTC" : zone}
              placeholder="UTC or ±HH:mm"
              onChange={({ detail }) =>
                composeDateTime(date, time, detail.value)
              }
            />
          </FormField>
        </div>
      </SpaceBetween>
    );
  }
  if (type === "string" && ["eq", "ne"].includes(criterion.operator))
    return scalarControl(
      <Autosuggest
        ref={registerFocus}
        expandToViewport
        ariaLabel={label}
        ariaLabelledby=""
        value={criterion.value || ""}
        disabled={!criterion.column}
        placeholder={
          values.length ? "Choose or enter a value" : "Enter an exact value"
        }
        options={options}
        filteringType="auto"
        clearAriaLabel={`Clear value ${index + 1}`}
        selectedAriaLabel="Selected"
        enteredTextLabel={(value) => `Use "${value}"`}
        empty="No suggested values."
        onChange={({ detail }) => change(detail.value)}
      />
    );
  return scalarControl(
    <Input
      ref={registerFocus}
      ariaLabel={label}
      value={criterion.value || ""}
      disabled={!criterion.column}
      onChange={({ detail }) => change(detail.value)}
    />
  );
}
